import PropTypes from "prop-types";
import { memo, useCallback, useContext, useEffect, useState } from "react";
import classNames from "classnames";
import { forceCheck } from "react-lazyload";
import camelCase from "lodash/camelCase";
import upperCase from "lodash/upperCase";
import includes from "lodash/includes";
import { FormattedDate, FormattedMessage } from "react-intl";
import { PRODUCTS_FILTERS_KEY } from "app/constants";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import {
	sendTagOnProductsFilter,
	sendTagOnProductsFilterOpen,
	sendTagOnProductsFilterRemove,
} from "app/utils/analytics";
import "./CategorizedProductsFilter.scss";
import IconArrowDown from "app/pages/.shared/static/icons/IconArrowDown";
import IconArrowUp from "app/pages/.shared/static/icons/IconArrowUp";
import AdvancedSelect from "app/pages/.shared/form/AdvancedSelect";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { ProductsMasterFilter } from "app/pages/Listing/ProductFilters/ProductsMasterFilter/ProductsMasterFilter";
import useClickAway from "app/utils/hooks/useClickAway";
import AppGlobalsContext from "app/AppGlobalsContext";

/**
 * Les icones des pictos des filtres TOPIC doivent être mis dans le répertoire icons > Incontournables de cloudinary
 * Le nom des icones est importante : on reprend le libellé du filtre et on met en MAJUSCULE et _ au lieu des espaces
 */
const CategorizedProductsFilter = props => {
	const {
		productsFilters = {},
		visibleFilters = {},
		selectedFacets = {},
		productsActiveFilters,
		setProductsFilter = () => {},
		unsetProductsFilter = () => {},
		destinationFilterValues = [],
		zoneFilterValues = [],
		topDestinationsFilterValues = [],
		topTopicFilterValues = [],
		topicFilterValues = [],
		periodFilterValues = [],
		monthsFilterValues = [],
		badgesFilterValues = [],
		scrollToListingHeader = () => {},
		className,
		filtersRef,
		isStuck,
		displayFilters = true,
	} = props;
	const { resolution } = useContext(AppGlobalsContext);
	const [selectedTab, setSelectedTab] = useState(-1); // par defaut aucun panneau filtre ne doit être ouvert

	const handleSelect = useCallback(
		index => {
			setSelectedTab(selectedTab === index ? -1 : index); // si on reclic sur un onglet, on ferme le panneau
		},
		[selectedTab]
	);

	useEffect(() => {
		forceCheck(); // forcer l'affichage des photos des produits filtrés après l'application d'un filtre
	}, [selectedTab]);

	useClickAway(filtersRef, () => {
		setSelectedTab(-1);
	});

	const filterClassname = classNames("categorized-products-filters", className, {
		"categorized-products-filters--stuck": isStuck,
	});

	const isDestinationFilterActive = productsFilters.destination;

	const isZoneFilterActive = productsFilters.zone;

	const isMonthFilterActive = productsFilters.month;

	const isTopicFilterActive = productsFilters.topic;

	const isBadgeFilterActive = productsFilters.badge;

	const isPeriodFilterActive = productsFilters.period;

	const isDiscountPercentageFacetActive = productsFilters.discountPercentage;

	const is48hFacetActive = productsFilters.endAt;

	const percentageCount = selectedFacets?.discountPercentage?.true
		? selectedFacets?.discountPercentage?.true?.length
		: 0;

	const percentageClassname = classNames({
		"filters-facets__item": true,
		"filters-facets__item--active": isDiscountPercentageFacetActive,
		"filters-facets__item--available": percentageCount > 0,
	});

	let topicTabHeaderLabel = <FormattedMessage id="listing.filter.theme.label" />;

	if (isTopicFilterActive) {
		topicTabHeaderLabel = (
			<div className="categorized-products-filters__filter-name">{productsFilters.topic}</div>
		);
	} else if (isBadgeFilterActive) {
		topicTabHeaderLabel = (
			<div className="categorized-products-filters__filter-name">{productsFilters.badge}</div>
		);
	} else if (isDiscountPercentageFacetActive) {
		topicTabHeaderLabel = (
			<div className="categorized-products-filters__filter-name">
				<FormattedMessage id="listing.filter.percentage" values={{ percentage: "50" }} />
			</div>
		);
	} else if (is48hFacetActive) {
		topicTabHeaderLabel = (
			<div className="products-filters__filter-name">
				<FormattedMessage id="listing.filter.48h" />
			</div>
		);
	}

	let periodTabHeaderLabel = <FormattedMessage id="listing.filter.calendrier.label" />;

	if (isMonthFilterActive) {
		periodTabHeaderLabel = (
			<div className="categorized-products-filters__filter-name">
				<FormattedDate value={Number(productsFilters.month)} month="long" year="numeric">
					{monthLabel => <div>{monthLabel}</div>}
				</FormattedDate>
			</div>
		);
	} else if (isPeriodFilterActive) {
		periodTabHeaderLabel = (
			<div className="categorized-products-filters__filter-name">
				{productsFilters.period}
			</div>
		);
	}

	let destinationTabIcon = (
		<IconArrowDown height={15} width={25} className="categorized-products-filters__icon" />
	);
	if (isDestinationFilterActive || isZoneFilterActive) {
		destinationTabIcon = (
			<i
				className="icon icon--circle-cross-black"
				onClick={e => {
					e.stopPropagation();
					unsetProductsFilter(PRODUCTS_FILTERS_KEY.DESTINATION);
					unsetProductsFilter(PRODUCTS_FILTERS_KEY.ZONE);
					sendTagOnProductsFilterRemove("destination");
					scrollToListingHeader();
				}}
			/>
		);
	} else if (selectedTab === 0) {
		destinationTabIcon = (
			<IconArrowUp height={15} width={25} className="categorized-products-filters__icon" />
		);
	}

	let topicTabIcon = (
		<IconArrowDown height={15} width={25} className="categorized-products-filters__icon" />
	);
	if (
		isTopicFilterActive ||
		isBadgeFilterActive ||
		isDiscountPercentageFacetActive ||
		is48hFacetActive
	) {
		topicTabIcon = (
			<i
				className="icon icon--circle-cross-black"
				onClick={e => {
					e.stopPropagation();
					unsetProductsFilter(PRODUCTS_FILTERS_KEY.TOPIC);
					unsetProductsFilter(PRODUCTS_FILTERS_KEY.BADGE);
					unsetProductsFilter(PRODUCTS_FILTERS_KEY.DISCOUNT_PERCENTAGE);
					unsetProductsFilter(PRODUCTS_FILTERS_KEY.END_AT);
					sendTagOnProductsFilterRemove("theme");
					scrollToListingHeader();
				}}
			/>
		);
	} else if (selectedTab === 2) {
		topicTabIcon = (
			<IconArrowUp height={15} width={25} className="categorized-products-filters__icon" />
		);
	}

	let periodTabIcon = (
		<IconArrowDown height={15} width={25} className="categorized-products-filters__icon" />
	);
	if (isPeriodFilterActive || isMonthFilterActive) {
		periodTabIcon = (
			<i
				className="icon icon--circle-cross-black"
				onClick={e => {
					e.stopPropagation();
					unsetProductsFilter(PRODUCTS_FILTERS_KEY.PERIOD);
					unsetProductsFilter(PRODUCTS_FILTERS_KEY.MONTH);
					sendTagOnProductsFilterRemove("calendar");
					scrollToListingHeader();
				}}
			/>
		);
	} else if (selectedTab === 1) {
		periodTabIcon = (
			<IconArrowUp height={15} width={25} className="categorized-products-filters__icon" />
		);
	}

	const destinationTabClass = classNames({
		"categorized-products-filters__header": true,
		"categorized-products-filters__header--active":
			isDestinationFilterActive || isZoneFilterActive,
	});

	const topicTabClass = classNames({
		"categorized-products-filters__header": true,
		"categorized-products-filters__header--active":
			isTopicFilterActive ||
			isBadgeFilterActive ||
			isDiscountPercentageFacetActive ||
			is48hFacetActive,
	});

	const periodTabClass = classNames({
		"categorized-products-filters__header": true,
		"categorized-products-filters__header--active": isPeriodFilterActive || isMonthFilterActive,
	});

	const destinationOptions = destinationFilterValues.map(destination => {
		return { value: destination, label: destination };
	});

	if (displayFilters) {
		return resolution === RESOLUTION.MEDIUM || resolution === RESOLUTION.SMALL ? (
			<div className={filterClassname} data-testid="products-filters" ref={filtersRef}>
				<ProductsMasterFilter {...props} />
			</div>
		) : (
			<div className={filterClassname} data-testid="products-filters" ref={filtersRef}>
				<Tabs
					className="categorized-products-filters__container"
					selectedIndex={selectedTab}
					onSelect={handleSelect}
				>
					<TabList
						className={[
							"categorized-products-filters__headers",
							"react-tabs__tab-list",
						]}
					>
						<Tab
							className={[destinationTabClass, "react-tabs__tab"]}
							onClick={() => {
								handleSelect(0);
								unsetProductsFilter(PRODUCTS_FILTERS_KEY.DESTINATION);
								unsetProductsFilter(PRODUCTS_FILTERS_KEY.ZONE);
								sendTagOnProductsFilterOpen("destination");
							}}
						>
							{!isDestinationFilterActive && !isZoneFilterActive ? (
								<FormattedMessage id="listing.filter.destination.label" />
							) : (
								<div className="categorized-products-filters__filter-name">
									{productsFilters.destination} {productsFilters.zone}
								</div>
							)}

							{destinationTabIcon}
						</Tab>
						<Tab
							className={[periodTabClass, "react-tabs__tab"]}
							onClick={() => {
								handleSelect(1);
								unsetProductsFilter(PRODUCTS_FILTERS_KEY.PERIOD);
								unsetProductsFilter(PRODUCTS_FILTERS_KEY.MONTH);
								sendTagOnProductsFilterOpen("calendar");
							}}
						>
							{periodTabHeaderLabel}

							{periodTabIcon}
						</Tab>
						<Tab
							className={[topicTabClass, "react-tabs__tab"]}
							onClick={() => {
								handleSelect(2);
								unsetProductsFilter(PRODUCTS_FILTERS_KEY.TOPIC);
								unsetProductsFilter(PRODUCTS_FILTERS_KEY.BADGE);
								unsetProductsFilter(PRODUCTS_FILTERS_KEY.DISCOUNT_PERCENTAGE);
								sendTagOnProductsFilterOpen("theme");
							}}
						>
							{topicTabHeaderLabel}

							{topicTabIcon}
						</Tab>
					</TabList>

					<TabPanel
						selectedClassName="categorized-products-filters__content--selected"
						className={[
							"categorized-products-filters__content",
							"categorized-products-filters__content--destination",
							"react-tabs__tab-panel",
						]}
						key={`destination-${selectedTab}`}
					>
						<div>
							<div className="filters-facets">
								<div className="filters-facets__title">
									<FormattedMessage id="listing.filter.destination.top.subtitle" />
								</div>
								<ul className="filters-facets__list">
									{topDestinationsFilterValues.map(topDestination => {
										const destinationKey = camelCase(topDestination.code);
										const available = includes(
											visibleFilters.destination,
											topDestination.label
										);
										const active = includes(
											productsActiveFilters,
											topDestination.label
										);
										const count = selectedFacets.destination[
											topDestination.label
										]
											? selectedFacets.destination[topDestination.label]
													.length
											: 0;
										const facetClassName = classNames({
											"filters-facets__item": true,
											"filters-facets__item--active": active,
											"filters-facets__item--available": available,
										});

										return (
											count > 0 && (
												<li
													className={facetClassName}
													key={destinationKey}
													onClick={() => {
														setProductsFilter({
															[PRODUCTS_FILTERS_KEY.DESTINATION]:
																topDestination.label,
														});
														handleSelect(-1); // fermeture de l'onglet

														sendTagOnProductsFilter(
															"topDestination",
															topDestination.label
														);
														scrollToListingHeader();
													}}
												>
													<div>
														{topDestination.label} ({count})
													</div>
												</li>
											)
										);
									})}
								</ul>
							</div>
							<AdvancedSelect
								placeholder={
									<FormattedMessage id="listing.filter.destination.select.placeholder" />
								}
								noOptionsMessage={() => (
									<FormattedMessage id="listing.filter.destination.empty.select.placeholder" />
								)}
								value={destinationOptions.find(destination => {
									return destination.value === productsFilters.destination;
								})}
								getOptionValue={({ value }) => value}
								getOptionLabel={({ label, value }) => {
									const count = selectedFacets.destination[value]
										? selectedFacets.destination[value].length
										: 0;

									return `${label} (${count})`;
								}}
								blurInputOnSelect={true}
								isClearable={false}
								onChange={({ value }) => {
									setProductsFilter({
										[PRODUCTS_FILTERS_KEY.DESTINATION]: value,
									});
									handleSelect(-1); // fermeture de l'onglet
									sendTagOnProductsFilter("destination", value);
									scrollToListingHeader();
								}}
								options={destinationOptions}
							/>
						</div>

						<div className="filters-facets">
							<div className="filters-facets__title">
								<FormattedMessage id="listing.filter.destination.areas.subtitle" />
							</div>
							<ul className="filters-facets__list">
								{zoneFilterValues.map(zone => {
									const destinationKey = camelCase(zone);
									const available = includes(visibleFilters.zone, zone);
									const active = includes(productsActiveFilters, zone);
									const count = selectedFacets?.zone[zone]
										? selectedFacets.zone[zone].length
										: 0;
									const facetClassName = classNames({
										"filters-facets__item": true,
										"filters-facets__item--active": active,
										"filters-facets__item--available": available,
									});

									return (
										count > 0 && (
											<li
												className={facetClassName}
												key={destinationKey}
												onClick={() => {
													setProductsFilter({
														[PRODUCTS_FILTERS_KEY.ZONE]: zone,
													});
													handleSelect(-1); // fermeture de l'onglet
													sendTagOnProductsFilter("zone", zone);
													scrollToListingHeader();
												}}
											>
												<div>
													{zone} ({count})
												</div>
											</li>
										)
									);
								})}
							</ul>
						</div>
					</TabPanel>

					<TabPanel
						selectedClassName="categorized-products-filters__content--selected"
						className={[
							"categorized-products-filters__content",
							"categorized-products-filters__content--period",
							"react-tabs__tab-panel",
						]}
						key={`period-${selectedTab}`}
					>
						{visibleFilters.month.length === 0 && visibleFilters.period === 0 ? (
							<FormattedMessage id="listing.filter.no.period.facet.message" />
						) : (
							false
						)}

						{visibleFilters.month.length !== 0 || visibleFilters.period !== 0 ? (
							<div className="filters-facets">
								<div className="filters-facets__title">
									<FormattedMessage id="listing.filter.month.label" />
								</div>
								<div className="filters-facets__list">
									{Object.keys(monthsFilterValues).map(year => {
										return (
											<div key={year}>
												<div className="filters-facets__subtitle">
													{year}
												</div>
												<div className="filters-facets__months">
													{monthsFilterValues[year].map(
														monthTimestamp => {
															const today = new Date();

															const monthDate = new Date(
																Number(monthTimestamp)
															);
															const monthIndex = monthDate.getUTCMonth();

															if (monthTimestamp) {
																const available = includes(
																	visibleFilters.month,
																	monthTimestamp
																);
																const active = includes(
																	productsActiveFilters,
																	monthTimestamp
																);
																const count = selectedFacets.month[
																	monthTimestamp
																]
																	? selectedFacets.month[
																			monthTimestamp
																	  ].length
																	: 0;
																const facetClassName = classNames({
																	"filters-facets__item": true,
																	"filters-facets__item--active": active,
																	"filters-facets__item--available": available,
																});

																return (
																	count > 0 && (
																		<div
																			className={
																				facetClassName
																			}
																			key={monthIndex}
																			onClick={() => {
																				setProductsFilter({
																					[PRODUCTS_FILTERS_KEY.MONTH]: monthTimestamp,
																				});
																				handleSelect(-1); // fermeture de l'onglet
																				sendTagOnProductsFilter(
																					"month",
																					monthTimestamp
																				);
																				scrollToListingHeader();
																			}}
																		>
																			<FormattedDate
																				value={Number(
																					monthTimestamp
																				)}
																				month="long"
																			>
																				{monthLabel => (
																					<div>
																						{monthLabel}{" "}
																						({count})
																					</div>
																				)}
																			</FormattedDate>
																		</div>
																	)
																);
															}
															return (
																<div
																	className="filters-facets__item"
																	key={monthIndex}
																>
																	<FormattedDate
																		timeZone="UTC"
																		value={
																			new Date(
																				today.getUTCFullYear(),
																				monthIndex,
																				15
																			)
																		}
																		month="long"
																	>
																		{monthLabel => (
																			<div>
																				{monthLabel} ({0})
																			</div>
																		)}
																	</FormattedDate>
																</div>
															);
														}
													)}
												</div>
											</div>
										);
									})}
								</div>
							</div>
						) : (
							false
						)}

						{visibleFilters.month.length !== 0 || visibleFilters.period !== 0 ? (
							<div className="filters-facets">
								<div className="filters-facets__title">
									<FormattedMessage id="listing.filter.period.label" />
								</div>
								<ul className="filters-facets__list">
									{periodFilterValues.map(period => {
										const periodKey = camelCase(period);
										const available = includes(visibleFilters.period, period);
										const active = includes(productsActiveFilters, period);
										const count = selectedFacets.period[period]
											? selectedFacets.period[period].length
											: 0;
										const facetClassName = classNames({
											"filters-facets__item": true,
											"filters-facets__item--active": active,
											"filters-facets__item--available": available,
										});

										return (
											count > 0 && (
												<div
													className={facetClassName}
													key={periodKey}
													onClick={() => {
														setProductsFilter({
															[PRODUCTS_FILTERS_KEY.PERIOD]: period,
														});
														handleSelect(-1); // fermeture de l'onglet
														sendTagOnProductsFilter("period", period);
														scrollToListingHeader();
													}}
												>
													<div>
														{period} ({count})
													</div>
												</div>
											)
										);
									})}
								</ul>
							</div>
						) : (
							false
						)}
					</TabPanel>

					<TabPanel
						selectedClassName="categorized-products-filters__content--selected"
						className={[
							"categorized-products-filters__content",
							"categorized-products-filters__content--topic",
							"react-tabs__tab-panel",
						]}
						key={`topic-${selectedTab}`}
					>
						<div className="filters-facets">
							<div className="filters-facets__title">
								<FormattedMessage id="listing.filter.topic.tendances.subtitle" />
							</div>
							<ul className="filters-facets__list">
								{topTopicFilterValues.map(topic => {
									const topicKey = camelCase(topic);
									const available = includes(visibleFilters.topic, topic);
									const active = includes(productsActiveFilters, topic);
									const count = selectedFacets.topic[topic]
										? selectedFacets.topic[topic].length
										: 0;
									const facetClassName = classNames({
										"filters-facets__item": true,
										"filters-facets__item--active": active,
										"filters-facets__item--available": available,
									});

									return (
										count > 0 && (
											<li
												className={facetClassName}
												key={topicKey}
												onClick={() => {
													setProductsFilter({
														[PRODUCTS_FILTERS_KEY.TOPIC]: topic,
													});
													handleSelect(-1); // fermeture de l'onglet
													sendTagOnProductsFilter("theme", topic);
													scrollToListingHeader();
												}}
											>
												{topic} ({count})
											</li>
										)
									);
								})}

								{percentageCount > 0 && (
									<li
										className={percentageClassname}
										onClick={() => {
											unsetProductsFilter(PRODUCTS_FILTERS_KEY.BADGE);
											unsetProductsFilter(PRODUCTS_FILTERS_KEY.TOPIC);
											setProductsFilter({
												[PRODUCTS_FILTERS_KEY.DISCOUNT_PERCENTAGE]: true,
											});
											handleSelect(-1); // fermeture de l'onglet
											sendTagOnProductsFilter("theme", "50%");
											scrollToListingHeader();
										}}
									>
										<FormattedMessage
											id="listing.filter.percentage"
											values={{ percentage: "50" }}
										/>
										<span className="filters-facets__item--count">
											({percentageCount})
										</span>
									</li>
								)}
							</ul>
						</div>

						<div className="filters-facets">
							<div className="filters-facets__title">
								<FormattedMessage id="listing.filter.product.type.subtitle" />
							</div>
							<ul className="filters-facets__list">
								{topicFilterValues.map(topic => {
									const topicKey = camelCase(topic);
									const available = includes(visibleFilters.topic, topic);
									const active = includes(productsActiveFilters, topic);
									const count = selectedFacets.topic[topic]
										? selectedFacets.topic[topic].length
										: 0;
									const facetClassName = classNames({
										"filters-facets__item": true,
										"filters-facets__item--active": active,
										"filters-facets__item--available": available,
									});

									const pictoUrl = `https://res.cloudinary.com/perfectstay/image/upload/v1/icons/Incontournables/${upperCase(
										topic
									).replace(/\s/g, "_")}.svg`;

									return (
										count > 0 && (
											<div
												className={facetClassName}
												key={topicKey}
												onClick={() => {
													setProductsFilter({
														[PRODUCTS_FILTERS_KEY.TOPIC]: topic,
													});
													handleSelect(-1); // fermeture de l'onglet
													sendTagOnProductsFilter("theme", topic);
													scrollToListingHeader();
												}}
											>
												<img src={pictoUrl} />
												{topic} ({count})
											</div>
										)
									);
								})}
							</ul>
						</div>

						<div className="filters-facets">
							<div className="filters-facets__title">
								<FormattedMessage id="listing.filter.topic.hotelType.subtitle" />
							</div>
							<ul className="filters-facets__list">
								{badgesFilterValues.map(badge => {
									const badgeKey = camelCase(badge);
									const available = includes(visibleFilters.badge, badge);
									const active = includes(productsActiveFilters, badge);
									const count = selectedFacets.badge[badge]
										? selectedFacets.badge[badge].length
										: 0;
									const facetClassName = classNames({
										"filters-facets__item": true,
										"filters-facets__item--active": active,
										"filters-facets__item--available": available,
									});

									return (
										count > 0 && (
											<div
												className={facetClassName}
												key={badgeKey}
												onClick={() => {
													setProductsFilter({
														[PRODUCTS_FILTERS_KEY.BADGE]: badge,
													});
													handleSelect(-1); // fermeture de l'onglet
													sendTagOnProductsFilter("badge", badge);
													scrollToListingHeader();
												}}
											>
												<div>
													{badge} ({count})
												</div>
											</div>
										)
									);
								})}
							</ul>
						</div>
					</TabPanel>
				</Tabs>
			</div>
		);
	}
	return null;
};

CategorizedProductsFilter.propTypes = {
	setProductsFilter: PropTypes.func,
	unsetProductsFilter: PropTypes.func,
	visibleFilters: PropTypes.object,
	selectedFacets: PropTypes.object,
	productsActiveFilters: PropTypes.arrayOf(PropTypes.string),
	zoneFilterValues: PropTypes.arrayOf(PropTypes.string),
	destinationFilterValues: PropTypes.arrayOf(PropTypes.string),
	topDestinationsFilterValues: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string,
			code: PropTypes.string,
		})
	),
	periodFilterValues: PropTypes.arrayOf(PropTypes.string),
	productsFilters: PropTypes.object,
	topicFilterValues: PropTypes.arrayOf(PropTypes.string),
	monthsFilterValues: PropTypes.shape({
		[PropTypes.string]: PropTypes.arrayOf(PropTypes.string),
	}),
	badgesFilterValues: PropTypes.arrayOf(PropTypes.string),
	topTopicFilterValues: PropTypes.arrayOf(PropTypes.object),
	scrollToListingHeader: PropTypes.func,
	filtersRef: PropTypes.object,
	className: PropTypes.string,
	isStuck: PropTypes.bool,
	displayFilters: PropTypes.bool,
};

export default memo(CategorizedProductsFilter);
